// Push Notification Subscription Management
// Handles subscribing, unsubscribing, and checking push notification state

// Convert URL-safe Base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function getVapidPublicKey(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="vapid-public-key"]')?.content || null
}

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function getPermissionState(): NotificationPermission | 'unavailable' {
  if (!('Notification' in window)) return 'unavailable'
  return Notification.permission
}

export async function isCurrentlySubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  try {
    // Use getRegistration instead of .ready — .ready hangs forever if no SW is registered
    const registration = await navigator.serviceWorker.getRegistration('/')
    if (!registration?.active) return false
    const subscription = await registration.pushManager.getSubscription()
    return subscription !== null
  } catch {
    return false
  }
}

export interface PushSupportDetails {
  supported: boolean
  serviceWorkerSupported: boolean
  pushManagerSupported: boolean
  notificationSupported: boolean
  permission: NotificationPermission | 'unavailable'
  isSecureContext: boolean
  isStandalone: boolean
  reason?: string
}

export function getPushSupportDetails(): PushSupportDetails {
  const sw = 'serviceWorker' in navigator
  const pm = 'PushManager' in window
  const notif = 'Notification' in window
  const secure = window.isSecureContext
  const standalone = window.matchMedia('(display-mode: standalone)').matches

  const details: PushSupportDetails = {
    supported: sw && pm && notif && secure,
    serviceWorkerSupported: sw,
    pushManagerSupported: pm,
    notificationSupported: notif,
    permission: notif ? Notification.permission : 'unavailable',
    isSecureContext: secure,
    isStandalone: standalone
  }

  if (!secure) details.reason = 'Push notifications require a secure (HTTPS) connection.'
  else if (!sw) details.reason = 'Your browser does not support service workers.'
  else if (!pm) details.reason = 'Your browser does not support the Push API.'
  else if (!notif) details.reason = 'Your browser does not support the Notification API.'

  return details
}

export async function subscribeToPush(): Promise<{ success: boolean; error?: string }> {
  if (!isPushSupported()) return { success: false, error: 'Push not supported in this browser' }

  const vapidKey = getVapidPublicKey()
  if (!vapidKey) {
    return { success: false, error: 'VAPID key not configured — server may need restart' }
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { success: false, error: `Notification permission ${permission}. Check browser/OS notification settings.` }
    }
  } catch (e) {
    return { success: false, error: `Permission request failed: ${e}` }
  }

  // Ensure service worker is registered, with a timeout
  let registration: ServiceWorkerRegistration
  try {
    // First try to get existing registration
    const existing = await navigator.serviceWorker.getRegistration('/')
    if (existing?.active) {
      registration = existing
    } else {
      // Try registering fresh
      registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
      // Wait for it to activate, with timeout
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker activation timed out')), 10000))
      ]) as ServiceWorkerRegistration
      registration = (await navigator.serviceWorker.getRegistration('/'))!
    }
  } catch (e) {
    return { success: false, error: `Service worker failed: ${e}` }
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    })

    const response = await fetch('/push_subscriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`)
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: `Subscription failed: ${error}` }
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return true // already unsubscribed

    await subscription.unsubscribe()

    await fetch('/push_subscriptions', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    }).catch(() => {}) // Best effort server cleanup

    return true
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error)
    return false
  }
}
